import React, { useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle, CheckCircle, AlertTriangle, Star } from 'lucide-react';

const FAQ = () => {
  const [openItems, setOpenItems] = useState(new Set([0])); // First FAQ open by default

  const toggleItem = (index) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  const faqs = [
    {
      question: "Why should I choose Brit Ember over other casino review sites?",
      answer: "Brit Ember stands apart through our unwavering commitment to excellence. Unlike generic review sites that list every operator, we meticulously curate only the most prestigious platforms. Our recommendations are backed by deep industry expertise, rigorous verification processes, and a player-first philosophy that ensures every suggestion delivers genuine value and outstanding experiences.",
      icon: <Star className="h-5 w-5" />,
      category: "trust"
    },
    {
      question: "Are the casinos you recommend safe and licensed?",
      answer: "Absolutely. Every platform featured on Brit Ember holds valid licenses from reputable regulatory authorities. We conduct thorough due diligence, including license verification, financial stability assessments, and reputation analysis. Your security and fair play are non-negotiable – we only partner with operators who meet our stringent standards for player protection and responsible gaming.",
      icon: <CheckCircle className="h-5 w-5" />,
      category: "safety"
    },
    {
      question: "What makes these welcome bonuses so special?",
      answer: "Our featured bonuses aren't just generous – they're strategically designed to enhance your gaming experience. Whether it's Winamax's €250 bonus with €100 in free bets, Zeturf's €60 starter bonus plus €250 in free bets, or Bwin's €100 welcome offer, each is crafted to provide real value while ensuring fair wagering requirements and genuine gaming enjoyment.",
      icon: <CheckCircle className="h-5 w-5" />,
      category: "bonuses"
    },
    {
      question: "How do I know which casino is right for me?",
      answer: "Consider your preferences: sports enthusiasts will thrive with Winamax's comprehensive betting platform, horse racing aficionados will appreciate Zeturf's French racing expertise, while those seeking a complete entertainment package will find Bwin's global markets and live casino irresistible. Our detailed reviews highlight each platform's unique strengths to help you make an informed choice.",
      icon: <HelpCircle className="h-5 w-5" />,
      category: "choice"
    },
    {
      question: "What about responsible gaming and player protection?",
      answer: "Responsible gaming is fundamental to Brit Ember's philosophy. All recommended operators provide comprehensive tools for setting deposit limits, self-exclusion options, and reality checks. We actively promote healthy gaming habits and provide resources for those who need additional support. Remember: gaming should always be entertaining, never problematic.",
      icon: <AlertTriangle className="h-5 w-5" />,
      category: "responsible"
    },
    {
      question: "Can I trust the payout speeds and withdrawal processes?",
      answer: "Yes, with confidence. Our featured platforms prioritize fast, reliable payouts. Winamax and Bwin typically process withdrawals within 24 hours, while Zeturf maintains competitive payout times for their horse racing payouts. All operators use secure, encrypted payment methods and adhere to strict financial regulations ensuring your winnings reach you safely and promptly.",
      icon: <CheckCircle className="h-5 w-5" />,
      category: "payments"
    }
  ];

  const getCategoryColor = (category) => {
    switch (category) {
      case 'trust': return 'border-l-book-gold bg-gradient-to-r from-yellow-50 to-yellow-100';
      case 'safety': return 'border-l-green-500 bg-gradient-to-r from-green-50 to-green-100';
      case 'bonuses': return 'border-l-purple-500 bg-gradient-to-r from-purple-50 to-purple-100';
      case 'choice': return 'border-l-blue-500 bg-gradient-to-r from-blue-50 to-blue-100';
      case 'responsible': return 'border-l-orange-500 bg-gradient-to-r from-orange-50 to-orange-100';
      case 'payments': return 'border-l-teal-500 bg-gradient-to-r from-teal-50 to-teal-100';
      default: return 'border-l-book-brown bg-book-beige';
    }
  };

  return (
    <section id="faq" className="book-section bg-book-cream">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-book-brown mb-6">
            Your Questions, Expertly Answered
          </h2>
          <p className="text-xl text-book-brown-dark max-w-3xl mx-auto">
            Discover why discerning players choose Brit Ember for their gaming journey.
            We're here to address your concerns with transparency, expertise, and genuine care for your experience.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className={`rounded-xl shadow-book border-l-4 overflow-hidden transition-all duration-200 ${
                openItems.has(index) ? 'shadow-lg' : ''
              } ${getCategoryColor(faq.category)}`}
            >
              <button
                onClick={() => toggleItem(index)}
                className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-opacity-50 transition-colors duration-200"
              >
                <div className="flex items-center space-x-3">
                  <div className={`text-${faq.category === 'trust' ? 'yellow' : faq.category === 'safety' ? 'green' : faq.category === 'bonuses' ? 'purple' : faq.category === 'choice' ? 'blue' : faq.category === 'responsible' ? 'orange' : 'teal'}-600`}>
                    {faq.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-book-brown pr-4">
                    {faq.question}
                  </h3>
                </div>
                <div className="flex-shrink-0">
                  {openItems.has(index) ? (
                    <ChevronUp className="h-5 w-5 text-book-brown" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-book-brown" />
                  )}
                </div>
              </button>

              {openItems.has(index) && (
                <div className="px-6 pb-4">
                  <div className="border-t border-gray-200 pt-4">
                    <p className="text-book-brown-dark leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <div className="bg-book-beige rounded-xl p-8 shadow-book">
            <HelpCircle className="h-12 w-12 mx-auto mb-4 text-book-brown" />
            <h3 className="text-2xl font-bold text-book-brown mb-4">Still Have Questions?</h3>
            <p className="text-lg text-book-brown-dark mb-6 max-w-2xl mx-auto">
              Our team of gaming experts is ready to provide personalized guidance.
              Whether you're new to online casinos or seeking advanced strategies, we're here to help you succeed.
            </p>
            <a
              href="#contact"
              className="inline-flex items-center space-x-2 book-button"
            >
              <span>Contact Our Experts</span>
              <HelpCircle className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
